import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Confirm,
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  BlogCategory,
  BlogPost,
  CmsPageMarket,
  TipTapDoc,
} from '../dataProvider';
import { SLUG_PATTERN, emptyDoc } from '../cms/cmsModel';
import { RichTextEditor } from '../cms/RichTextEditor';

/**
 * The blog post authoring form (D-cms-blog). REUSES the cmsPages spine and the SAME
 * TipTap rich-text body (the SAME RichTextEditor, not a second editor) PLUS the blog
 * fields: a single category (over the blogCategories vocabulary), a featured-image URL,
 * a byline author and an excerpt. SIMPLE draft / published lifecycle (REUSED from
 * cmsPages, NOT versioned): SAVE writes the content and returns the post to DRAFT;
 * PUBLISH (cms:publish) stamps it live and sets publishedAt (which is ALSO the displayed
 * post date). Authoring gates cms:author, operational market-scoped (market is
 * Market | 'all'). All mutations run through the audited provider; publish routes through
 * the chokepoint.
 */

const BlogPostFormInner = ({ record }: { record?: BlogPost }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();
  const { role, accessibleRegions } = useAccessContext();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );
  // A post may target a single in-scope market OR both (the RuleMarket idiom).
  const marketChoices: CmsPageMarket[] = [...markets, 'all'];

  // The category vocabulary feeds the single-select (the post points at a stable key).
  const { data: categories } = useGetList<BlogCategory>('blogCategories', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'label', order: 'ASC' },
  });

  const [title, setTitle] = useState(record?.title ?? '');
  const [slug, setSlug] = useState(record?.slug ?? '');
  const [market, setMarket] = useState<CmsPageMarket>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );
  const [categoryKey, setCategoryKey] = useState(record?.categoryKey ?? '');
  const [featuredImageUrl, setFeaturedImageUrl] = useState(
    record?.featuredImageUrl ?? '',
  );
  const [author, setAuthor] = useState(record?.author ?? '');
  const [excerpt, setExcerpt] = useState(record?.excerpt ?? '');
  const [body, setBody] = useState<TipTapDoc>(record?.body ?? emptyDoc());
  const [pendingPublish, setPendingPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  const canPublish = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:publish' },
  );

  // Default the category once the vocabulary loads (a new post starts on the first key).
  const effectiveCategoryKey = categoryKey || (categories?.[0]?.key ?? '');

  const validate = (): string | null => {
    if (!title.trim()) return 'Title is required';
    if (!SLUG_PATTERN.test(slug)) {
      return 'Slug must be lowercase words joined by hyphens';
    }
    if (!effectiveCategoryKey) return 'A category is required';
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    const nowIso = new Date().toISOString();
    const shared = {
      title,
      slug,
      market,
      categoryKey: effectiveCategoryKey,
      featuredImageUrl,
      author,
      excerpt,
      body,
      status: 'draft' as const,
      publishedAt: null,
      updatedAt: nowIso,
    };
    try {
      if (isEdit && record) {
        // SAVE returns the post to DRAFT (edits are not live until re-published).
        await dataProvider.update('blogPosts', {
          id: record.id,
          data: shared,
          previousData: record,
        });
        notify('Draft saved', { type: 'success' });
        redirect('list', 'blogPosts');
      } else {
        const { data } = await dataProvider.create('blogPosts', {
          data: {
            ...shared,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
          },
        });
        notify('Post created', { type: 'success' });
        redirect('edit', 'blogPosts', data.id);
      }
    } catch {
      notify('Could not save the post', { type: 'error' });
    }
  };

  // PUBLISH the SAVED post to live (a distinct capability through the chokepoint).
  const confirmPublish = async () => {
    if (!record) return;
    setBusy(true);
    const publishedAt = new Date().toISOString();
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'cms:publish',
        resource: 'blogPosts',
        id: record.id,
        data: { status: 'published', publishedAt },
        previousData: record,
      });
      notify('Post published', { type: 'success' });
      setPendingPublish(false);
      refresh();
    } catch {
      notify('Could not publish the post', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit post' : 'New post'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'blog-title' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              helperText="lowercase-hyphenated"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'blog-slug' }}
            />
            <TextField
              select
              label="Category"
              value={effectiveCategoryKey}
              onChange={(e) => setCategoryKey(e.target.value)}
              margin="dense"
              size="small"
              sx={{ minWidth: 200 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'blog-category' }}
            >
              {(categories ?? []).map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as CmsPageMarket)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'blog-market' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m === 'all' ? 'Both markets' : m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Featured image URL"
            value={featuredImageUrl}
            onChange={(e) => setFeaturedImageUrl(e.target.value)}
            helperText="A URL to the hero image (no upload)"
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'blog-featured-image' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              helperText="The byline (may credit a brand or guest)"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'blog-author' }}
            />
          </Stack>
          <TextField
            label="Excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            helperText="An optional summary for the post list and social previews"
            margin="dense"
            size="small"
            fullWidth
            multiline
            inputProps={{ 'data-testid': 'blog-excerpt' }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Body
          </Typography>
          <RichTextEditor value={body} onChange={setBody} />
        </CardContent>
      </Card>

      {isEdit && record && (
        <Card sx={{ mb: 2 }} data-testid="blog-publish">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="subtitle2">Status</Typography>
              <Chip
                size="small"
                color={record.status === 'published' ? 'success' : 'default'}
                variant={record.status === 'published' ? 'filled' : 'outlined'}
                label={record.status === 'published' ? 'Published' : 'Draft'}
                data-testid="blog-status-chip"
              />
              {record.publishedAt && (
                <Typography variant="caption" color="text.secondary">
                  Live since {record.publishedAt.slice(0, 10)}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              The marketing site serves the published post. Saving an edit
              returns the post to draft until you publish it again.
            </Typography>
            {canPublish && (
              <Button
                variant="contained"
                disabled={busy || record.status === 'published'}
                onClick={() => setPendingPublish(true)}
                data-testid="blog-publish-button"
              >
                Publish
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="blog-save">
          {isEdit ? 'Save draft' : 'Create post'}
        </Button>
        <Button onClick={() => redirect('list', 'blogPosts')}>Cancel</Button>
      </Stack>

      <Confirm
        isOpen={pendingPublish}
        loading={busy}
        title="Publish this post"
        content="This publishes the saved post to live. The marketing site will serve it. Saving a later edit returns it to draft."
        confirm="Confirm"
        cancel="Cancel"
        onConfirm={confirmPublish}
        onClose={() => setPendingPublish(false)}
      />
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const BlogPostCreateView = () => <BlogPostFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const BlogPostEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<BlogPost>('blogPosts', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <BlogPostFormInner record={record} />;
};

export const BlogPostCreate = () => (
  <RequireAccess capability="cms:author">
    <BlogPostCreateView />
  </RequireAccess>
);

export const BlogPostEdit = () => (
  <RequireAccess capability="cms:author">
    <BlogPostEditView />
  </RequireAccess>
);
